// Üst kimlik şeridi. Solda VKN/TCKN + Sorgula ve altında unvan; sağ dar sütunda grup, cari kodu, durum.
// Kişi türü seçimi başlıktadır (PersonTypeToggle).
import React from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Link, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import type { AccountCardForm } from '../useAccountCardForm';
import { L } from './fields';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
`;

/** Bu işaretli öğelere tıklamak kişi türü uyarısını göstermez (türün kendisi, rehber, kapat, vazgeç). */
export const TYPE_HINT_SKIP = 'data-type-hint-skip';

/**
 * Başlıktaki kişi türü seçimi. hint açıkken altında geçici bir uyarı balonu çıkar ve düğmeler yanıp söner;
 * tür seçilmeden kartta başka bir yere tıklandığında AddAccount açar.
 */
export const PersonTypeToggle: React.FC<{ f: AccountCardForm; hint?: boolean; onHintClose?: () => void }> = ({
  f, hint = false, onHintClose,
}) => {
  const { t } = useTranslation('accounting');
  const missing = !f.accountData.personType;
  return (
    <Tooltip
      open={hint && missing}
      onClose={onHintClose}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      // Tema genel bir açılma gecikmesi veriyor; uyarı tıklamayla anında görünmeli
      enterDelay={0}
      enterNextDelay={0}
      leaveDelay={0}
      arrow
      placement="bottom-end"
      title={(
        <Box sx={{ p: 0.5 }}>
          <Typography variant="body2" fontWeight={700}>
            {t(`${L}.layout.typeHintTitle`, 'Önce kişi türünü seçin')}
          </Typography>
          <Typography variant="caption" component="div">
            {t(`${L}.layout.typeHintText`, 'Tüzel kişi veya Gerçek kişi seçmelisiniz. VKN / TCKN yazarsanız tür kendiliğinden seçilir.')}
          </Typography>
        </Box>
      )}
      componentsProps={{
        tooltip: { sx: { bgcolor: 'error.main', maxWidth: 280, boxShadow: 3 } },
        arrow: { sx: { color: 'error.main' } },
      }}
    >
      <ToggleButtonGroup
        {...{ [TYPE_HINT_SKIP]: 'true' }}
        size="small"
        exclusive
        color="primary"
        value={f.accountData.personType || null}
        onChange={(_, v) => { if (v) f.handlePersonTypeChange(v); }}
        aria-label={t('current.personType.label')}
        sx={{
          '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, py: 0.5, fontWeight: 600 },
          '& .Mui-selected, & .Mui-selected:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
          ...(missing ? { outline: '1px solid', outlineColor: 'error.main', borderRadius: 1 } : {}),
          ...(hint && missing ? { animation: `${pulse} 1.2s ease-out 3` } : {}),
        }}
      >
        <ToggleButton value="LEGAL">{t('current.personType.legal')}</ToggleButton>
        <ToggleButton value="REAL">{t('current.personType.real')}</ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );
};

const LookupLine: React.FC<{ f: AccountCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');
  const r = f.lookup;
  if (!r) {
    return (
      <Typography variant="caption" color="text.secondary">
        {t(`${L}.layout.taxHint`, '10 hane VKN tüzel, 11 hane TCKN gerçek kişi seçer.')}
      </Typography>
    );
  }
  switch (r.status) {
    case 'invalid':
      return (
        <Typography variant="caption" color="error">
          {t(`${L}.lookup.invalid`, 'VKN 10 hane, TC Kimlik No 11 hane olmalıdır.')}
        </Typography>
      );
    case 'no-integration':
      return (
        <Typography variant="caption" color="text.secondary">
          {t(`${L}.lookup.noIntegrationShort`, 'Sorgu için e-Fatura entegrasyonu gerekli.')}{' '}
          <Link component="button" type="button" variant="caption" onClick={() => f.setNoIntegrationOpen(true)}>
            {t(`${L}.lookup.howTo`, 'Nasıl eklenir?')}
          </Link>
        </Typography>
      );
    case 'found':
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          <Chip size="small" color="success" variant="outlined" label={t(`${L}.lookup.eInvoice`, 'e-Fatura mükellefi')} />
          {r.alias && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.alias}</Typography>}
          <Typography variant="caption" color="text.secondary">
            · {t(`${L}.lookup.via`, '{{provider}} ile sorgulandı', { provider: r.provider })}
          </Typography>
        </Box>
      );
    case 'not-einvoice':
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
          <Chip size="small" color="info" variant="outlined" label={t(`${L}.lookup.eArchive`, 'e-Arşiv')} />
          <Typography variant="caption" color="text.secondary">
            {t(`${L}.lookup.notEInvoice`, 'e-Fatura mükellefi değil; faturaları e-Arşiv olarak kesilir.')}
          </Typography>
        </Box>
      );
    case 'error':
    default:
      return (
        <Typography variant="caption" color="warning.main">
          {t(`${L}.lookup.failed`, 'Sorgu yapılamadı')}{r.status === 'error' && r.message ? `: ${r.message}` : ''}
        </Typography>
      );
  }
};

export const IdentityStrip: React.FC<{ f: AccountCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');
  const d = f.accountData;
  const groupOptions = f.getFilteredGroupOptions();
  const nameLabel = f.isRealPerson ? t(`${L}.fields.fullName`) : t(`${L}.fields.companyTitle`);
  const taxLabel = f.isRealPerson
    ? t(`${L}.fields.identityNumber`)
    : f.isLegalPerson
      ? t(`${L}.fields.taxNumber`)
      : t(`${L}.layout.taxOrIdentity`, 'VKN / TC Kimlik No');
  const showErrors = !!f.error;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 260px' }, gap: 1.5 }}>
      {/* Sol: VKN + Sorgula, unvan */}
      <Box sx={{ display: 'grid', gap: 1.25, alignContent: 'start' }}>
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              id="account-card-tax"
              fullWidth
              required={f.isLegalPerson}
              label={taxLabel}
              value={d.taxNumber}
              error={showErrors && f.isLegalPerson && d.taxNumber.length !== 10}
              onChange={(e) => f.handleTaxNumberChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); f.runLookup(); } }}
              inputProps={{ maxLength: 11, inputMode: 'numeric' }}
              sx={{ bgcolor: 'background.paper' }}
            />
            <Button
              variant="contained"
              onClick={f.runLookup}
              disabled={f.lookupLoading}
              sx={{ height: 56, minWidth: 104, flex: 'none', textTransform: 'none', fontWeight: 700 }}
            >
              {f.lookupLoading ? <CircularProgress size={20} color="inherit" /> : t(`${L}.lookup.button`, 'Sorgula')}
            </Button>
          </Box>
          <Box sx={{ mt: 0.5, minHeight: 20 }}><LookupLine f={f} /></Box>
        </Box>

        <Box>
          <TextField
            id="account-card-name"
            fullWidth
            required
            label={d.personType ? nameLabel : t(`${L}.fields.companyName`)}
            value={d.companyName}
            onChange={(e) => f.handleChange('companyName', e.target.value)}
            error={showErrors && !d.companyName.trim()}
            sx={{ bgcolor: 'background.paper' }}
          />
          {f.titleSuggestion && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t(`${L}.lookup.gibTitle`, 'GİB unvanı')}: <strong>{f.titleSuggestion}</strong>{' '}
              <Link component="button" type="button" variant="caption" onClick={f.applyLookupTitle}>
                {t(`${L}.lookup.useTitle`, 'Bunu kullan')}
              </Link>
            </Typography>
          )}
        </Box>
      </Box>

      {/* Sağ: grup, cari kodu, durum */}
      <Box sx={{ display: 'grid', gap: 1.25, alignContent: 'start' }}>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Autocomplete
            id="account-card-group"
            fullWidth
            size="small"
            options={groupOptions}
            isOptionEqualToValue={(o, v) => o.group.id === v.group.id}
            getOptionLabel={(o) => (o.level === 1 ? o.label : `${o.parentName} > ${o.label}`)}
            value={f.selectedGroup ? (groupOptions.find(o => o.group.id === f.selectedGroup!.id) || {
              group: f.selectedGroup,
              label: f.localizeGroupName(f.selectedGroup.name),
              level: f.selectedGroup.level,
              isParent: f.selectedGroup.level === 1,
              parentName: f.getGroupFullPath(f.selectedGroup).split(' > ').slice(0, -1).join(' > ') || undefined,
            }) : null}
            onChange={(_, v) => f.handleGroupChange(v ? v.group : null)}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={t(`${L}.fields.group`)}
                error={showErrors && !d.groupId}
                sx={{ bgcolor: 'background.paper' }}
              />
            )}
            renderOption={(props, o) => (
              <li {...props} key={o.group.id}>
                <Box sx={{ pl: (o.level - 1) * 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon fontSize="small" sx={{ color: o.isParent ? 'primary.main' : 'action.active' }} />
                  <Typography variant="body2" fontWeight={o.isParent ? 700 : 400}>{o.label}</Typography>
                </Box>
              </li>
            )}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={f.handleOpenGroupModal}
            aria-label={t(`${L}.layout.addGroup`, 'Yeni grup')}
            title={t(`${L}.layout.addGroup`, 'Yeni grup')}
            sx={{ minWidth: 40, px: 0, bgcolor: 'background.paper' }}
          >
            <AddIcon fontSize="small" />
          </Button>
        </Box>
        <TextField
          fullWidth
          size="small"
          label={t(`${L}.fields.accountCode`)}
          placeholder={f.isEditMode ? undefined : t(`${L}.layout.accountCodeAuto`, 'otomatik')}
          InputLabelProps={f.isEditMode ? undefined : { shrink: true }}
          value={d.accountCode}
          onChange={(e) => f.handleChange('accountCode', e.target.value)}
          sx={{ bgcolor: 'background.paper' }}
        />
        <TextField
          select
          fullWidth
          size="small"
          label={t(`${L}.layout.status`, 'Durum')}
          value={f.isActive ? 'active' : 'passive'}
          onChange={(e) => f.setIsActive(e.target.value === 'active')}
          sx={{ bgcolor: 'background.paper' }}
        >
          <MenuItem value="active">{t(`${L}.layout.active`, 'Aktif')}</MenuItem>
          <MenuItem value="passive">{t(`${L}.layout.passive`, 'Pasif')}</MenuItem>
        </TextField>
      </Box>
    </Box>
  );
};
